import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Plus, Trash2, Edit3, Copy, Check, X, Tag, Shield, Loader2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  code: "",
  description: "",
  discount_percent: 100,
  max_uses: 0,
  valid_until: "",
  is_active: true,
  is_internal: false,
};

export default function PromoCodesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | id
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/agency/promo-codes");
      setItems(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    try {
      const payload = { ...form, valid_until: form.valid_until || null };
      if (editing === "new") await api.post("/agency/promo-codes", payload);
      else await api.put(`/agency/promo-codes/${editing}`, payload);
      toast.success(editing === "new" ? "Code créé" : "Code mis à jour");
      setEditing(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const del = async (id, code) => {
    if (!window.confirm(`Supprimer le code ${code} ?`)) return;
    await api.delete(`/agency/promo-codes/${id}`);
    toast.success("Supprimé");
    await load();
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success(`${code} copié`);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="promo-codes-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.22em] font-bold text-[color:var(--brand-sky)] mb-1">
            <Tag className="w-3.5 h-3.5" /> CODES PROMO
          </div>
          <h1 className="font-display text-3xl font-bold text-slate-900">Codes promotionnels</h1>
          <p className="text-sm text-slate-500 mt-1 max-w-2xl">
            Créez des codes de remise pour les clients ou des codes 100% gratuits pour vos sites internes.
            Utilisez <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded text-xs">MSC-FREE-100</code> pour créer un site sans payer.
          </p>
        </div>
        <Button
          onClick={() => { setEditing("new"); setForm(EMPTY); }}
          className="bg-[color:var(--brand-navy,#0F2744)]"
          data-testid="new-promo-btn"
        >
          <Plus className="w-4 h-4 mr-1" /> Nouveau code
        </Button>
      </header>

      {editing && (
        <Card className="p-6 space-y-3 border-2 border-[color:var(--brand-sky,#38BDF8)]">
          <div className="flex items-center justify-between mb-2">
            <h2 className="font-bold text-lg">{editing === "new" ? "Nouveau code promo" : "Modifier le code"}</h2>
            <Button size="sm" variant="ghost" onClick={() => setEditing(null)}><X className="w-4 h-4" /></Button>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Code (UPPERCASE)</Label>
              <Input
                value={form.code}
                onChange={e => setForm({ ...form, code: e.target.value.toUpperCase() })}
                placeholder="MSC-FREE-100"
                className="font-mono"
                data-testid="promo-code-input"
              />
            </div>
            <div>
              <Label className="text-xs">Remise (%)</Label>
              <Input
                type="number"
                min="1"
                max="100"
                value={form.discount_percent}
                onChange={e => setForm({ ...form, discount_percent: +e.target.value })}
                data-testid="promo-discount-input"
              />
            </div>
          </div>
          <div>
            <Label className="text-xs">Description (visible dans le back-office)</Label>
            <Textarea
              rows={2}
              value={form.description}
              onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="Ex: Code de lancement printemps 2026 — 20% sur le setup fee"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs">Nombre max d'utilisations (0 = illimité)</Label>
              <Input
                type="number"
                min="0"
                value={form.max_uses}
                onChange={e => setForm({ ...form, max_uses: +e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs">Date d'expiration (optionnel)</Label>
              <Input
                type="date"
                value={form.valid_until ? form.valid_until.slice(0, 10) : ""}
                onChange={e => setForm({ ...form, valid_until: e.target.value ? new Date(e.target.value).toISOString() : "" })}
              />
            </div>
          </div>
          <div className="flex items-center gap-6 flex-wrap pt-2 border-t border-slate-200">
            <div className="flex items-center gap-2">
              <Switch checked={form.is_active} onCheckedChange={v => setForm({ ...form, is_active: v })} />
              <Label className="text-xs">Code actif</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch checked={form.is_internal} onCheckedChange={v => setForm({ ...form, is_internal: v })} />
              <Label className="text-xs flex items-center gap-1">
                <Shield className="w-3 h-3 text-amber-500" />
                Code interne agence (pas comptabilisé en CA)
              </Label>
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
            <Button onClick={save} className="bg-[color:var(--brand-navy,#0F2744)]" data-testid="promo-save-btn">
              <Check className="w-4 h-4 mr-1" /> Enregistrer
            </Button>
          </div>
        </Card>
      )}

      {loading ? (
        <div className="py-20 text-center"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
      ) : items.length === 0 ? (
        <Card className="p-12 text-center">
          <Tag className="w-10 h-10 mx-auto text-slate-300 mb-2" />
          <div className="text-sm text-slate-500">Aucun code promo. Créez-en un pour offrir des remises.</div>
        </Card>
      ) : (
        <div className="space-y-2" data-testid="promo-codes-list">
          {items.map(p => (
            <Card key={p.id} className="p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`promo-row-${p.code}`}>
              <div className="flex items-center gap-3 min-w-0 flex-1">
                <button
                  type="button"
                  onClick={() => copyCode(p.code)}
                  className="font-mono font-bold text-sm bg-slate-100 hover:bg-slate-200 px-3 py-1.5 rounded-lg flex items-center gap-1.5 transition"
                  title="Cliquer pour copier"
                >
                  {p.code} <Copy className="w-3 h-3 opacity-50" />
                </button>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <Badge className={`text-xs ${p.discount_percent === 100 ? "bg-emerald-100 text-emerald-700" : "bg-blue-100 text-blue-700"} border-0`}>
                      -{p.discount_percent}%
                    </Badge>
                    {p.is_internal && (
                      <Badge className="bg-amber-100 text-amber-700 border-0 text-[10px] uppercase tracking-wider flex items-center gap-1">
                        <Shield className="w-3 h-3" /> Interne
                      </Badge>
                    )}
                    {!p.is_active && <Badge variant="outline" className="text-[10px] uppercase tracking-wider text-slate-400">Inactif</Badge>}
                  </div>
                  {p.description && <p className="text-xs text-slate-500 mt-0.5 truncate">{p.description}</p>}
                  <div className="text-[10px] text-slate-400 mt-0.5">
                    Utilisations : {p.uses_count}{p.max_uses > 0 ? ` / ${p.max_uses}` : " (illimité)"}
                    {p.valid_until && ` · Expire le ${new Date(p.valid_until).toLocaleDateString("fr-FR")}`}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setEditing(p.id); setForm({ ...p, valid_until: p.valid_until || "" }); }}
                  data-testid={`promo-edit-${p.code}`}
                >
                  <Edit3 className="w-3.5 h-3.5" />
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => del(p.id, p.code)}
                  className="text-red-600"
                  data-testid={`promo-delete-${p.code}`}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </Button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
